import clsx from "clsx";
import { useEffect, useState } from "react";

import { ServiceReserveStatus } from "@/apis/clients";
import { RadioCardType } from "@/components/atoms/inputForms/RadioCardType";
import { MenuTemplateType } from "@/states/page-editor/template-parts/service-menu";
import { useHandleFieldValueChange } from "@/states/service-form/operations";
import {
  ConfirmServiceInfo,
  useConfirmInputList,
} from "@/states/service-form/selectors";
import {
  ServiceFormServiceInputKey,
  SubscriptionServiceDetail,
  SubscriptionServiceInfo,
} from "@/states/service-form/types";
import { formatNumber } from "@/utils/number";

interface MenuSubscriptionViewItemsProps {
  menus: SubscriptionServiceDetail[];
  reserveStatus: ServiceReserveStatus;
  service: SubscriptionServiceInfo | undefined;
  type: MenuTemplateType;
  isConfirm?: boolean;
  isViewSeatsLeft?: boolean;
}

export function MenuSubscriptionViewItems({
  menus,
  reserveStatus,
  service,
  type,
  isConfirm = false,
  isViewSeatsLeft,
}: MenuSubscriptionViewItemsProps) {
  const confirmInputList = useConfirmInputList();

  const onFieldValueChange =
    useHandleFieldValueChange<ServiceFormServiceInputKey>();
  const handleDisableInput = (
    item: SubscriptionServiceDetail | ConfirmServiceInfo["details"][0],
  ) => {
    return item.remainingReservationCount !== undefined &&
      item.remainingReservationCount <= 0;
  };

  const [optionIdsSelected, setOptionIdsSelected] = useState<number[]>([]);
  const [quantity, setQuantity] = useState<{ [key: string]: number }>({});

  const DEFAULT_QUANTITY = 1;

  useEffect(() => {
    const _quantity = isConfirm
      ? confirmInputList.serviceDraft?.quantity || {}
      : confirmInputList.service?.quantity || {};

    if (Object.keys(_quantity).length > 0) {
      // Check if _quantity is different from current quantity to prevent loop
      const isDifferent = Object.keys(_quantity).some(key => _quantity[key] !== quantity[key]) || Object.keys(quantity).length !== Object.keys(_quantity).length;
      if (isDifferent) {
        setQuantity(_quantity);
        onFieldValueChange("quantity", _quantity);
      }
      return;
    }

    if (menus && menus.length > 0) {
      const _quantity = Object.fromEntries(
        menus.map((item) => [
          item.id,
          item.maximumNumber ? DEFAULT_QUANTITY : 0,
        ]),
      );
      // Check if _quantity is different from current quantity to prevent loop
      const isDifferent = Object.keys(_quantity).some(key => _quantity[key] !== quantity[key]) || Object.keys(quantity).length !== Object.keys(_quantity).length;
      if (isDifferent) {
        setQuantity(_quantity);
        onFieldValueChange("quantity", _quantity);
      }
    }
  }, [
    confirmInputList.serviceDraft?.quantity,
    confirmInputList.service?.quantity,
    isConfirm,
    onFieldValueChange,
    menus,
  ]);

  const detailIds = isConfirm
    ? confirmInputList.serviceDraft?.details
      ?.filter((detail) => !handleDisableInput(detail))
      .map((item) => item.id) || []
    : confirmInputList.service?.details
      ?.filter((detail) => !handleDisableInput(detail))
      .map((item) => item.id) || [];

  useEffect(() => {
    if (menus.length === 1) {
      if (menus[0].remainingReservationCount !== 0) {
        // Prevent loop by checking if detailIds already contains the menu id
        if (!detailIds.includes(Number(menus[0].id))) {
          onFieldValueChange("detailIds", [Number(menus[0].id)]);
        }
      }
    }
  }, [onFieldValueChange, reserveStatus, menus, type, detailIds]);
  const isSelected = (id: number) => {
    if (optionIdsSelected.length > 0) {
      return optionIdsSelected.includes(id);
    }
    return detailIds.includes(id);
  };

  const containerClass = (item: SubscriptionServiceDetail) =>
    !(isSelected(Number(item.id)) || handleDisableInput(item))
      ? "bg-white"
      : "";

  const checkIsShowWarning = ({ menuId }: { menuId: string }): boolean => {
    const cap = service?.remaining_capacity || 0;
    const getTotal = (ids: number[]) =>
      ids.reduce((sum, id) => sum + (quantity[id] ?? 0), 0);

    switch (reserveStatus) {
      case ServiceReserveStatus.SINGLE_SELECT:
        return isSelected(Number(menuId)) && quantity[menuId] > cap;

      default: {
        const allIds = menus.map((item) => Number(item.id));
        return getTotal(allIds) > cap;
      }
    }
  };

  useEffect(() => {
    const selectContainers = document.querySelectorAll(".select-container");
    if (selectContainers.length) {
      selectContainers.forEach((parent) => {
        const boxShowQuantity = parent.querySelector(".box-show-quantity");
        const selectQuantity = parent.querySelector(
          ".select-quantity",
        ) as HTMLElement;

        if (boxShowQuantity && selectQuantity) {
          const parentRect = parent.getBoundingClientRect();
          const childRect = boxShowQuantity.getBoundingClientRect();

          const distanceTop = childRect.top - parentRect.top;

          selectQuantity.style.top = `${distanceTop}px`;
        }
      });
    }
  }, []);
  return (
    <div className="space-y-4">
      {menus
        ?.filter((item) => {
          return item;
        })
        .map((item, index) => {
          const isDisabledItem = handleDisableInput(item);

          return (
            <div key={index}>
              <div className="select-container relative">
                <RadioCardType
                  id={`detailIds-${item.id}`}
                  name="detailIds"
                  value={item.id}
                  label={
                    <ItemContent
                      item={item}
                      isViewSeatsLeft={isViewSeatsLeft}
                      showWarning={checkIsShowWarning({
                        menuId: String(item.id),
                      })}
                    />
                  }
                  labelClassName="w-full"
                  containerClassName={clsx(
                    "w-full !items-start -mt-1 !py-[10px] !px-[6px] !opacity-100",
                    containerClass(item),
                    {
                      "all-boxShowQuantity-color-gray": isDisabledItem,
                    },
                  )}
                  radioClassName=""
                  onChange={(value) => {
                    setOptionIdsSelected([Number(value)]);
                    onFieldValueChange("detailIds", [Number(value)]);
                  }}
                  checked={isSelected(Number(item.id))}
                  isDisabled={isDisabledItem}
                />
              </div>
            </div>
          );
        })}
    </div>
  );
}

interface ItemContentProps {
  item: SubscriptionServiceDetail;
  isViewSeatsLeft?: boolean;
  showWarning?: boolean;
  classNames?: string;
}
const ItemContent = ({
  item,
  isViewSeatsLeft,
  classNames = "",
}: ItemContentProps) => {
  const {
    name = "",
    price,
    description,
    maximumNumber,
    remainingReservationCount,
    isSetCapacity,
    capacity,
    isSetContractPeriod,
    contractPeriod,
    isSetFreeTrialPeriod,
    freeTrialPeriod,
  } = item;

  const isSoldOut = false;

  return (
    <div className="w-full ">
      <div className="w-full flex">
        <div className="flex-1">
          <p
            className={`font-bold text-gray-900 text-[16px] mb-2 break-word  leading-5 ${classNames}`}
          >
            {name}
          </p>
        </div>
        {isSoldOut && (
          <div className="border-[4px] border-[#D32932] border-solid rounded-[6px] !text-[#D32932] h-[56px] w-[68px] flex items-center justify-center text-[24px] font-bold">
            満席
          </div>
        )}
      </div>

      <div className="flex flex-row items-start justify-between">
        {isSetContractPeriod && (
          <p className="mb-2 text-[14px] shrink-0">{`期間  ${contractPeriod}ヶ月`}</p>
        )}

        <p className="w-full text-right text-[14px] mb-2">
          {isSetCapacity && maximumNumber !== undefined && (
            <span className="mr-2">
              {isViewSeatsLeft && remainingReservationCount !== undefined
                ? `残り${formatNumber(remainingReservationCount) ?? ""}名/`
                : ""}
              {`定員${formatNumber(maximumNumber) ?? ""}名`}
            </span>
          )}
          {price && price > 0 ? (
            <span className="font-bold text-[16px] ml-2">{`${formatNumber(
              price,
            )}円 (税込)`}</span>
          ) : null}
        </p>
      </div>

      <div className="flex flex-row items-start justify-end text-[#C00000]">
        {isSetFreeTrialPeriod && (
          <p className="mb-2 text-[16px] font-bold">{`無料トライアル ${freeTrialPeriod}日間`}</p>
        )}
      </div>

      {description && (
        <div className="w-full border-t border-zinc-100 py-2 flex flex-col gap-2">
          <p className="text-sm text-zinc-600 leading-5 break-word">{description}</p>
        </div>
      )}

      {/* {showWarning && 
        <div className="py-2 px-4 border border-solid border-[#FFE58F] bg-[#FFFBE6] text-xs rounded-lg flex gap-2">
          <CausionCircle className="w-4 h-4 shrink-0" />
          定員の残数に対して、チケット数が上限を超えました。お手数ですがチケット数を変更して申込みしてください。
        </div>
      } */}
    </div>
  );
};
